import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Avatar,
  Chip
} from '@mui/material';
import {
  Timer as TimerIcon,
} from '@mui/icons-material';

const useIdle = (timeout = 60000) => { // Default timeout of 1 minute
  const [isIdle, setIsIdle] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setIsIdle(false);
    timerRef.current = setTimeout(() => {
      setIsIdle(true);
    }, timeout);
  }, [timeout]);

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'wheel', 'mousedown', 'touchstart', 'touchmove'];

    events.forEach(event => {
      window.addEventListener(event, resetTimer);
    });

    // Initial setup
    resetTimer();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [resetTimer]);

  return isIdle;
};

interface SessionTimeoutModalProps {
  // Timeout duration in milliseconds before showing warning (default: 25 minutes)
  warningTime?: number;
  // Additional time after warning before auto-logout (default: 5 minutes)
  countdownTime?: number;
  // Callback when user confirms they're still active
  onStillActive: () => void;
  // Callback when session times out
  onTimeout: () => void;
  // Optional: enable/disable the modal
  enabled?: boolean;
}

export default function SessionTimeoutModal({
  warningTime = 25 * 60 * 1000, // 25 minutes
  countdownTime = 5 * 60 * 1000, // 5 minutes
  onStillActive,
  onTimeout,
  enabled = true
}: SessionTimeoutModalProps) {
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(countdownTime);
  const [progress, setProgress] = useState(100);

  // useRef to hold the countdown interval so it persists across renders
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  // local ref for remaining time to avoid stale closure issues
  const timeLeftRef = useRef<number>(countdownTime);

  const isIdle = useIdle(warningTime);

  useEffect(() => {
    // clear any existing interval helper
    const clearCountdownInterval = () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };

    if (isIdle) {
      setOpen(true);
      timeLeftRef.current = countdownTime;
      setCountdown(countdownTime);
      setProgress(100);

      // ensure previous interval is cleared before creating a new one
      clearCountdownInterval();

      countdownRef.current = setInterval(() => {
        timeLeftRef.current = Math.max(0, timeLeftRef.current - 1000);
        setCountdown(timeLeftRef.current);

        const newProgress = (timeLeftRef.current / countdownTime) * 100;
        setProgress(Math.max(0, newProgress));

        if (timeLeftRef.current <= 0) {
          clearCountdownInterval();
          setOpen(false);
          onTimeout();
        }
      }, 1000);
    } else {
      // user is active again: close modal and clear interval
      setOpen(false);
      clearCountdownInterval();
      timeLeftRef.current = countdownTime;
      setCountdown(countdownTime);
      setProgress(100);
    }

    return () => {
      // cleanup on unmount or when dependencies change
      clearCountdownInterval();
    };
  }, [isIdle, countdownTime, onTimeout]);

  const formatTime = (ms: number) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getProgressColor = () => {
    if (progress > 50) return 'primary';
    if (progress > 25) return 'warning';
    return 'error';
  };

  if (!enabled) return null;

  return (
    <Dialog
      open={open}
      onClose={() => { }} // Prevent closing by clicking outside
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown // Prevent closing with ESC key
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'warning.light' }}>
            <TimerIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Are You Still There?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Your session is about to expire
            </Typography>
          </Box>
          <Chip
            label={formatTime(countdown)}
            color={getProgressColor()}
            sx={{
              fontWeight: 'bold',
              fontSize: '1rem',
              height: 36,
              minWidth: 80
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Progress Bar */}
        <Box sx={{ mb: 3 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={getProgressColor()}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'grey.200'
            }}
          />
        </Box>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Your session will expire due to inactivity
          </Typography>
          <Typography variant="body2">
            For your security, we'll log you out automatically in <strong>{formatTime(countdown)}</strong>
          </Typography>
        </Alert>

        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>What happens if you don't respond?</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • You'll be logged out automatically<br />
            • Any unsaved changes may be lost<br />
            • You'll need to log in again to continue
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}